import React from "react";
import { useIsLoggedIn } from "@dynamic-labs/sdk-react-core";
import {
  Copy,
  ArrowUp,
  DollarSign,
  HeartHandshake,
  Download,
} from "lucide-react";
import isEmpty from "lodash.isempty";
import {
  GetBattleByIdApiResponse as Battle,
  BattlesType,
  ShareImageType,
} from "@dripit/types";

import XIcon from "@/assets/svg/x.svg?react";
import InstagramIcon from "@/assets/svg/instagram-2.svg?react";
import FacebookIcon from "@/assets/svg/facebook.svg?react";
import CoinsIcon from "@/assets/svg/coins-2.svg?react";
import ShareIcon from "@/assets/images/share-icon.png";
import {
  useToast,
  Button,
  DialogTitle,
  DrawerTitle,
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui";
import { DottedSeparator } from "@/components/main";
import { cn } from "@/helpers/tailwind";
import {
  generateHead2HeadShareImage,
  generateHypeboardShareImage,
  ShareInfoType,
} from "@/helpers/generateShareImage";
import { delay } from "@/helpers/delay";

enum Platform {
  X = "x",
  FB = "facebook",
  IG = "instagram",
}

const shareInfoList = [
  { id: ShareInfoType.Position, label: "Position", icon: ArrowUp },
  { id: ShareInfoType.PositionSize, label: "Position Size", icon: DollarSign },
  { id: ShareInfoType.Winnings, label: "Winnings", icon: CoinsIcon },
  {
    id: ShareInfoType.ReferralCode,
    label: "Referral Code-QR",
    icon: HeartHandshake,
  },
];

const socialPlatforms = [
  { id: Platform.X, label: "Share on X", icon: XIcon },
  { id: Platform.FB, label: "Share on Facebook", icon: FacebookIcon },
  { id: Platform.IG, label: "Share on Instagram", icon: InstagramIcon },
];

interface ShareBattleDialogContentProps {
  isDrawer?: boolean;
  battle?: Battle;
  onClose: () => void;
}

export const ShareBattleDialogContent: React.FC<
  ShareBattleDialogContentProps
> = ({ isDrawer = false, battle, onClose }) => {
  const { toast } = useToast();
  const isLoggedIn = useIsLoggedIn();
  const [selectedPlatform, setSelectedPlatform] = React.useState<Platform>(
    Platform.X
  );
  const [selectedInstagramType, setSelectedInstagramType] =
    React.useState<string>("post");
  const [selectedShareInfo, setSelectedShareInfo] = React.useState<
    ShareInfoType[]
  >([]);
  const [previewImage, setPreviewImage] = React.useState<string>("");
  const [isLoading, setIsLoading] = React.useState(false);
  const isMobileDevice = React.useMemo(
    () =>
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ),
    []
  );
  const TitleComponent = isDrawer ? DrawerTitle : DialogTitle;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast({
      title: "Link copied to clipboard",
    });
  };

  const handleCopyImage = async () => {
    if (previewImage) {
      try {
        const getImageBlob = async () => {
          const response = await fetch(previewImage);
          const blob = await response.blob();
          return blob;
        };
        await navigator.clipboard.write([
          new ClipboardItem({ "image/png": await getImageBlob() }),
        ]);
        toast({ title: "Image copied to clipboard" });
      } catch (err) {
        console.error("Error copying image:", err);
        if (err instanceof Error && err.name === "NotAllowedError") {
          toast({
            title: "Failed to copy - Not allowed",
            description: `Copying images to clipboard is not supported on your browser. Please long-press the image and select 'Copy' or 'Save'.`,
            variant: "destructive",
          });
        } else {
          toast({
            title: "Failed to copy image",
            description: `Please right click or long-press the image and select 'Copy' or 'Save'.`,
            variant: "destructive",
          });
        }
      }
    }
  };

  const handleShareInfoClick = (id: ShareInfoType) => {
    if (
      selectedShareInfo.includes(ShareInfoType.Position) &&
      id === ShareInfoType.Position
    ) {
      setSelectedShareInfo([
        ...selectedShareInfo.filter(
          (v) =>
            ![ShareInfoType.Position, ShareInfoType.PositionSize].includes(v)
        ),
      ]);
      return;
    }
    if (selectedShareInfo.includes(id)) {
      setSelectedShareInfo([...selectedShareInfo.filter((v) => v !== id)]);
      return;
    }
    if (
      !selectedShareInfo.includes(ShareInfoType.Position) &&
      [ShareInfoType.PositionSize, ShareInfoType.Winnings].includes(id)
    ) {
      setSelectedShareInfo([...selectedShareInfo, ShareInfoType.Position, id]);
      return;
    }

    setSelectedShareInfo([...selectedShareInfo, id]);
  };

  const handleShare = async () => {
    if (!previewImage) return;

    const response = await fetch(previewImage);
    const blob = await response.blob();
    const file = new File([blob], "battle-share.png", {
      type: "image/png",
    });

    if (isMobileDevice) {
      try {
        await navigator.share({
          files: [file],
          title: "Check out this battle!",
          text: battle?.name,
          url: window.location.href.split("?")[0],
        });
      } catch (err) {
        console.error("Error sharing:", err);
        if (err instanceof Error && err.name !== "AbortError") {
          toast({
            title: "Please upload the image manually",
            description: "Your browser doesn't support direct sharing",
            variant: "destructive",
          });
        }
      } finally {
        onClose();
        return;
      }
    }

    try {
      // copy image to clipboard
      await navigator.clipboard.write([
        new ClipboardItem({ [blob.type]: blob }),
      ]);
      toast({
        title: "Image copied!",
        description: "Paste the image in your post",
      });

      await delay(2000);

      let url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        "https://dripit.io" + window.location.pathname
      )}`;
      if (selectedPlatform === Platform.X) {
        url = `https://x.com/compose/tweet?text=${encodeURIComponent(
          `Check out this battle on DripIt! ${window.location.href.split("?")[0]}`
        )}`;
      }
      window.open(url, "_blank");
    } catch (err) {
      console.error("Error copying image:", err);
      toast({
        title: "Failed to share",
        description: "Please try sharing manually",
        variant: "destructive",
      });
    } finally {
      onClose();
      return;
    }
  };

  React.useEffect(() => {
    const generatePreview = async () => {
      if (!battle) return;
      setIsLoading(true);
      try {
        const imageType =
          selectedPlatform === Platform.IG
            ? selectedInstagramType === "post"
              ? ShareImageType.INSTAGRAM_POST
              : ShareImageType.INSTAGRAM_STORY
            : ShareImageType.TWITTER;

        let imageURL;
        if (battle.type === BattlesType.head2head_battle) {
          imageURL = await generateHead2HeadShareImage({
            battle,
            dimension: imageType,
            shareInfo: selectedShareInfo,
          });
        } else {
          imageURL = await generateHypeboardShareImage({
            battle,
            dimension: imageType,
            shareInfo: selectedShareInfo,
          });
        }

        if (imageURL) setPreviewImage(imageURL);
      } finally {
        setIsLoading(false);
      }
    };

    generatePreview();
  }, [battle, selectedPlatform, selectedInstagramType, selectedShareInfo]);

  return (
    <>
      <div className="absolute -top-16 left-1/2 -translate-x-1/2">
        <div className="rounded-full bg-slate-800 p-2">
          <img src={ShareIcon} alt="Share" className="h-20 w-20" />
        </div>
      </div>

      <div
        className={cn("p-4 text-center", {
          "scrollbar-thin my-6 max-h-[calc(100%-80px-48px)] overflow-y-auto":
            isDrawer,
        })}>
        <TitleComponent className="mb-1 mt-4 text-lg font-semibold lg:mt-0 lg:text-2xl">
          Share Battle with Friends
        </TitleComponent>
        <p className="text-md font-normal text-slate-300 lg:text-lg">
          The battle is happening right now! Share with your friends to join in.
        </p>
        <DottedSeparator className="my-6 w-full" />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-2">
          <div className="text-left">
            <h3 className="mb-2 text-sm font-bold">Share to</h3>
            <div className="flex items-center gap-2">
              <ToggleGroup
                type="single"
                value={selectedPlatform}
                onValueChange={(value) =>
                  value && setSelectedPlatform(value as Platform)
                }
                className="inline-flex w-fit flex-row justify-start rounded-full bg-slate-300 p-[2px]">
                {socialPlatforms.map(({ id, label, icon: Icon }) =>
                  id === Platform.IG && !isMobileDevice ? null : (
                    <ToggleGroupItem
                      key={id}
                      value={id}
                      aria-label={label}
                      className="rounded-full bg-slate-300 text-slate-900 transition-all hover:bg-slate-200 hover:text-slate-900 data-[state=on]:bg-slate-900 data-[state=on]:text-white data-[state=on]:shadow-[-2px_4px_8px_0px_rgba(0,0,0,0.25)]">
                      <Icon className="h-5 w-5" />
                    </ToggleGroupItem>
                  )
                )}
              </ToggleGroup>
              {selectedPlatform === Platform.IG && (
                <ToggleGroup
                  type="single"
                  value={selectedInstagramType}
                  onValueChange={(value) =>
                    value && setSelectedInstagramType(value)
                  }
                  className="inline-flex w-fit flex-row justify-start rounded-full border border-slate-700 bg-slate-800 p-[2px]">
                  <ToggleGroupItem
                    value="post"
                    aria-label="Instagram Post"
                    className="rounded-full bg-slate-800 text-slate-500 transition-all hover:bg-slate-700 hover:text-slate-400 data-[state=on]:border data-[state=on]:border-slate-700 data-[state=on]:bg-slate-500 data-[state=on]:text-white data-[state=on]:shadow-[-2px_4px_8px_0px_rgba(0,0,0,0.25)]">
                    Post
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="story"
                    aria-label="Instagram Story"
                    className="rounded-full bg-slate-800 text-slate-500 transition-all hover:bg-slate-700 hover:text-slate-400 data-[state=on]:border data-[state=on]:border-slate-700 data-[state=on]:bg-slate-500 data-[state=on]:text-white data-[state=on]:shadow-[-2px_4px_8px_0px_rgba(0,0,0,0.25)]">
                    Story
                  </ToggleGroupItem>
                </ToggleGroup>
              )}
            </div>

            <h3 className="mb-2 mt-6 text-sm font-bold">Include</h3>
            <div className="flex flex-wrap gap-1">
              {shareInfoList.map(({ id, label, icon: Icon }) => {
                const isWinningsButton = id === ShareInfoType.Winnings;
                const isPositionButton = id === ShareInfoType.Position;
                const isPositionSizeButton = id === ShareInfoType.PositionSize;
                const isReferralCodeButton = id === ShareInfoType.ReferralCode;
                const hasWinnings = battle?.claims?.some(
                  (claim) => claim.amount && Number(claim.amount) > 0
                );
                const hasPosition = !isEmpty(battle?.userPositions);

                const isDisabled =
                  (isWinningsButton && !hasWinnings) ||
                  (isPositionButton && !hasPosition) ||
                  (isPositionSizeButton && !hasPosition) ||
                  (isReferralCodeButton && !isLoggedIn);

                return (
                  <Button
                    key={id}
                    size="sm"
                    variant="secondary"
                    disabled={isDisabled}
                    className={cn(
                      "duration-250 rounded-full border transition-all ease-in-out",
                      "hover:bg-initial h-min flex-shrink-0 !px-2.5 !py-[6px]",
                      "border-slate-700 bg-slate-800 text-slate-500",
                      {
                        "text-slate-white border-slate-500 bg-slate-700":
                          selectedShareInfo.includes(id),
                        "cursor-not-allowed opacity-50": isDisabled,
                      }
                    )}
                    onClick={() => {
                      if (isDisabled) return;
                      handleShareInfoClick(id);
                    }}>
                    <Icon className="h-[14px] w-[14px]" />
                    <span>{label}</span>
                  </Button>
                );
              })}
            </div>

            <h3 className="mb-2 mt-6 text-sm font-bold">Copy the link:</h3>
            <div className="flex items-center gap-2 rounded-lg bg-slate-700 px-3">
              <span className="flex-1 truncate text-sm text-slate-200">
                {window.location.href}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 flex-shrink-0 hover:bg-slate-600"
                onClick={handleCopyLink}>
                <Copy className="h-4 w-4 text-slate-200" />
              </Button>
            </div>
          </div>
          <DottedSeparator className="block w-full md:hidden lg:hidden" />
          <div>
            <div className="flex items-center justify-between">
              <h3 className="mb-2 text-sm font-bold">Preview</h3>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  className="h-6 bg-white/15 px-2 text-xs font-normal hover:bg-white/20"
                  onClick={() => {
                    if (previewImage) {
                      const link = document.createElement("a");
                      link.href = previewImage;
                      link.download = `battle-share-${selectedPlatform}-${Date.now()}.png`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }
                  }}>
                  <Download className="h-[14px] w-[14px]" />
                  Save
                </Button>
                <Button
                  variant="ghost"
                  className="h-6 bg-white/15 px-2 text-xs font-normal hover:bg-white/20"
                  onClick={handleCopyImage}>
                  <Copy className="h-[14px] w-[14px]" />
                  Copy
                </Button>
              </div>
            </div>
            {previewImage && (
              <div className="mt-4 rounded-3xl bg-transparent p-0 md:bg-white/10 md:p-3">
                <div className="flex justify-center">
                  <div
                    className={cn(
                      "transition-all duration-300 ease-in-out",
                      isLoading ? "opacity-0" : "opacity-100",
                      "rounded-2xl shadow-[-8px_8px_30px_0px_rgba(0,0,0,0.25)]"
                    )}>
                    <img
                      src={previewImage}
                      alt="Share preview"
                      className={cn(
                        "h-auto rounded-2xl transition-all duration-300 ease-in-out",
                        isDrawer
                          ? "w-[389px]"
                          : selectedPlatform === Platform.IG
                            ? selectedInstagramType === "post"
                              ? "w-[260px]"
                              : "w-[180px]"
                            : "w-[389px]"
                      )}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className={cn("flex justify-center gap-3 px-4 pb-6 md:px-0 md:pb-2", {
          "items-center py-3 pb-3": isDrawer,
        })}>
        <Button
          variant="outline"
          className="w-full border border-slate-200 bg-slate-800 md:w-40 lg:w-40"
          onClick={onClose}>
          Cancel
        </Button>
        <Button
          className="w-lg:w-40 w-full bg-indigo-600 text-white hover:bg-indigo-700 md:w-40"
          onClick={async () => await handleShare()}>
          <span>Share on </span>
          {selectedPlatform === Platform.X ? (
            <XIcon className="h-4 w-4" />
          ) : selectedPlatform === Platform.IG ? (
            <InstagramIcon className="ml-1 h-4 w-4" />
          ) : (
            <FacebookIcon className="ml-1 h-4 w-4" />
          )}
        </Button>
      </div>
    </>
  );
};
